import {
  GAME_ID,
  REDUX_ACTIONS,
  DROPZONE_KEYS,
  QUESTION_TYPES,
  INTERACTION_TYPES,
} from '../../data/wordCityConstants';

const initialState = {
  uuid: null,
  questionId: null,
  // the correct word, the one being built towards
  targetWord: null,
  // the question word shown at the start
  referenceWord: null,
  // the letter to add or swap in; empty for REMOVER
  targetLetter: null,
  // the letter to get rid of; empty for ADDER
  referenceLetter: null,
  // what the child actually chose
  selectedLetter: null,
  referenceLetters: [],
  targetLetters: [],
  lettersBank: [],
  // ADDER adds a letter, CHANGER swaps one, REMOVER takes one away
  interactionType: null,
  // counts every drop, so a step missed and retried counts more than once
  movesAmount: 0,
  // null until answered, then false/true and never null again this step
  isAnswerCorrect: null,
  // the word the last drop produced
  userAnswer: null,
  isStepCompleted: false,
  hint1: null,
  hint2: null,
  phonemes1: [],
  phonemes2: [],
  questionType: null,
  // true once a miss has forfeited this step's topper
  isRewardLost: false,
};

const getAdderInteractionAnswer = (state, payload) => {
  const selectedLetter = payload.selectedLetter || '';

  switch (payload.dropZoneKey) {
    case DROPZONE_KEYS.DROPZONE_RIGHT: {
      return state.referenceWord + selectedLetter;
    }
    case DROPZONE_KEYS.DROPZONE_LEFT:
    default: {
      return selectedLetter + state.referenceWord;
    }
  }
};

const getChangerInteractionAnswer = (state, payload) => state.referenceLetters.map((letter, index) =>
  (index === payload.referenceLetterIndex ? payload.selectedLetter : letter),
).join('');

const getRemoverInteractionAnswer = (state, payload) => state.referenceLetters.map((letter, index) =>
  (index === payload.referenceLetterIndex ? '' : letter),
).join('');

/* Lets a drop be judged before it is dispatched, so feedback can fire on the gesture. Shares the
helpers the reducer uses and mirrors how it picks between them, so the two agree on every branch. */
export const getInteractionAnswer = (state, payload) => {
  switch (state.interactionType) {
    case INTERACTION_TYPES.ADDER:
      return getAdderInteractionAnswer(state, payload);
    case INTERACTION_TYPES.CHANGER:
      return getChangerInteractionAnswer(state, payload);
    case INTERACTION_TYPES.REMOVER:
      /* As SERVE_REMOVER_ANSWER does: a letter dropped during a remover step is a swap and already
      wrong, so it is scored the changer's way. Remover items do carry a letters bank, so this is a
      drop a child can actually make. */
      return payload.selectedLetter
        ? getChangerInteractionAnswer(state, payload)
        : getRemoverInteractionAnswer(state, payload);
    default:
      return null;
  }
};

const getServeAnswerNewState = (state, payload, userAnswer) => {
  const isAnswerCorrect = userAnswer === state.targetWord;
  const referenceLetters = isAnswerCorrect ? state.targetLetters : state.referenceLetters;

  return {
    ...state,
    referenceLetters: [...referenceLetters],
    selectedLetter: isAnswerCorrect ? payload.selectedLetter : state.selectedLetter,
    movesAmount: state.movesAmount + 1,
    isAnswerCorrect,
    userAnswer,
  };
};

const wordCityStandardStepReducer = (state = initialState, action) => {
  if (action.gameId !== GAME_ID) {
    return state;
  }

  switch (action.type) {
    case REDUX_ACTIONS.INIT_STANDARD_STEP: {
      const { targetLetters, referenceLetters } = action.payload;
      const targetWord = targetLetters.join('');
      const referenceWord = referenceLetters.join('');

      return {
        ...initialState,
        ...action.payload,
        referenceLetters: [...referenceLetters],
        targetLetters: [...targetLetters],
        referenceWord,
        targetWord,
        questionId: action.payload.questionId || targetWord,
        uuid: action.payload.uuid,
        questionType: action.payload.questionType || QUESTION_TYPES.STANDARD,
      };
    }
    case REDUX_ACTIONS.LOSE_TOPPER_REWARD: {
      return {
        ...state,
        isRewardLost: true,
      };
    }
    case REDUX_ACTIONS.SERVE_ADDER_ANSWER: {
      return getServeAnswerNewState(
        state,
        action.payload,
        getAdderInteractionAnswer(state, action.payload),
      );
    }
    case REDUX_ACTIONS.SERVE_CHANGER_ANSWER: {
      return getServeAnswerNewState(
        state,
        action.payload,
        getChangerInteractionAnswer(state, action.payload),
      );
    }
    case REDUX_ACTIONS.SERVE_REMOVER_ANSWER: {
      return getServeAnswerNewState(
        state,
        action.payload,
        // if user selected some letter during REMOVER interaction, this is ALREADY wrong
        // but we need to know user's relevant answer, so calculate it using CHANGER interaction method
        action.payload.selectedLetter ?
          getChangerInteractionAnswer(state, action.payload) : getRemoverInteractionAnswer(state, action.payload),
      );
    }
    case REDUX_ACTIONS.AUTO_COMPLETE_CORRECTLY: {
      return {
        ...state,
        referenceLetters: state.targetLetters,
        selectedLetter: state.selectedLetter,
        movesAmount: state.movesAmount + 1,
        isAnswerCorrect: true,
        userAnswer: `correct auto-complete: ${state.targetWord}`,
      };
    }
    case REDUX_ACTIONS.AUTO_COMPLETE_INCORRECTLY: {
      return {
        ...state,
        movesAmount: state.movesAmount + 1,
        isAnswerCorrect: false,
        userAnswer: 'incorrect auto-complete',
      };
    }

    case REDUX_ACTIONS.COMPLETE_STEP: {
      return {
        ...state,
        referenceWord: state.targetWord,
        isStepCompleted: true,
      };
    }
    default:
      return state;
  }
};

export default wordCityStandardStepReducer;
